import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { useT, LOCALE_LABELS, LOCALE_ORDER } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import {
  User,
  Shield,
  ShieldCheck,
  Globe,
  Palette,
  Key,
  Server,
  Save,
  Eye,
  EyeOff,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';
import PermissionMatrix from './PermissionMatrix';

const CURRENCY_OPTIONS = [
  ['USD', '$'], ['EUR', '€'], ['GBP', '£'], ['RUB', '₽'], ['UAH', '₴'], ['PLN', 'zł'], ['TRY', '₺'],
  ['BRL', 'R$'], ['MXN', '$'], ['JPY', '¥'], ['CNY', '¥'], ['INR', '₹'], ['THB', '฿'], ['AUD', '$'],
  ['CAD', '$'], ['CHF', 'Fr'],
];

export default function Settings() {
  const { user } = useAuth();
  const { t, locale, setLocale } = useT();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  const [profile, setProfile] = useState({ full_name: '', email: '' });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });

  const [userSettings, setUserSettings] = useState({
    timezone: 'UTC',
    language: 'en',
    email_notifications: true,
    sms_notifications: false,
    theme: 'dark',
    dashboard_refresh_rate: 30,
    date_format: 'DD.MM.YYYY',
    currency: 'USD',
  });


  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    setLoading(true);
    const [profileRes, userSettingsRes] = await Promise.allSettled([
      axios.get(`${API}/settings/profile`),
      axios.get(`${API}/settings/user`),
    ]);
    if (profileRes.status === 'fulfilled') {
      setProfile({ full_name: profileRes.value.data.full_name, email: profileRes.value.data.email });
    }
    if (userSettingsRes.status === 'fulfilled') {
      setUserSettings({ ...userSettingsRes.value.data, language: locale });
    }
    setLoading(false);
  };

  const updateProfile = async () => {
    try {
      await axios.put(`${API}/settings/profile`, profile);
      toast.success(t('Profile updated successfully'));
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || t('Error updating profile'));
    }
  };

  const changePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error(t('New passwords do not match'));
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error(t('New password must be at least 6 characters'));
      return;
    }
    try {
      const response = await axios.post(`${API}/settings/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      // Changing the password now ends every other session. The backend hands
      // back a fresh token for this device so the person who just proved they
      // know both passwords is not the one signed out.
      const nextToken = response?.data?.access_token;
      if (nextToken) {
        localStorage.setItem('token', nextToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${nextToken}`;
      }
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      toast.success(t('Password changed — other devices have been signed out'));
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || t('Error changing password'));
    }
  };

  const signOutEverywhere = async () => {
    // Deliberately signs this device out too. Per-device revocation needs
    // session records the backend does not keep yet, and a button that quietly
    // left one device signed in would be worse than no button.
    if (!window.confirm(t('Sign out of every device, including this one? You will need to sign in again.'))) {
      return;
    }
    try {
      await axios.post(`${API}/auth/logout-all`);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      toast.success(t('All sessions signed out'));
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out all sessions:', error);
      toast.error(error.response?.data?.detail || t('Error signing out sessions'));
    }
  };

  const updateUserSettings = async () => {
    try {
      await axios.put(`${API}/settings/user`, userSettings);
      // Apply the language locally as well
      if (userSettings.language && userSettings.language !== locale) {
        setLocale(userSettings.language);
      }
      toast.success(t('User settings saved'));
    } catch (error) {
      console.error('Error updating user settings:', error);
      toast.error(t('Error saving settings'));
    }
  };


  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="px-8 pt-2 pb-8 space-y-6">
      <XpnsrPageHeader
        kicker={t('Account')}
        title={t('Settings')}
        subtitle={t('Manage profile, preferences and system configuration.')}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex w-full flex-wrap h-auto justify-start">
          <TabsTrigger value="profile" className="flex items-center space-x-2">
            <User className="w-4 h-4" />
            <span>{t('Profile')}</span>
          </TabsTrigger>
          <TabsTrigger value="preferences" className="flex items-center space-x-2">
            <Palette className="w-4 h-4" />
            <span>{t('Preferences')}</span>
          </TabsTrigger>
          <TabsTrigger value="permissions" className="flex items-center space-x-2">
            <ShieldCheck className="w-4 h-4" />
            <span>{t('Permissions')}</span>
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center space-x-2">
            <Server className="w-4 h-4" />
            <span>{t('System')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <User className="w-5 h-5" />
                  <span>{t('Profile information')}</span>
                </CardTitle>
                <CardDescription>{t('Manage your basic account information')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">{t('Full name')}</Label>
                  <Input
                    id="full_name"
                    value={profile.full_name}
                    onChange={(e) => setProfile((prev) => ({ ...prev, full_name: e.target.value }))}
                    placeholder={t('Enter your full name')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('Email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder={t('Enter your email')}
                  />
                </div>
                <Button onClick={updateProfile} className="w-full">
                  <Save className="w-4 h-4 mr-2" />
                  {t('Save profile')}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Key className="w-5 h-5" />
                  <span>{t('Change password')}</span>
                </CardTitle>
                <CardDescription>{t('Update your password to keep your account secure')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'current', id: 'current_password', label: t('Current password'), placeholder: t('Enter current password') },
                  { key: 'new', id: 'new_password', label: t('New password'), placeholder: t('Enter new password') },
                  { key: 'confirm', id: 'confirm_password', label: t('Confirm new password'), placeholder: t('Confirm new password') },
                ].map(({ key, id, label, placeholder }) => (
                  <div className="space-y-2" key={key}>
                    <Label htmlFor={id}>{label}</Label>
                    <div className="relative">
                      <Input
                        id={id}
                        type={showPasswords[key] ? 'text' : 'password'}
                        value={passwordData[id]}
                        onChange={(e) => setPasswordData((prev) => ({ ...prev, [id]: e.target.value }))}
                        placeholder={placeholder}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1 h-8 w-8 p-0"
                        aria-label={showPasswords[key] ? t('Hide password') : t('Show password')}
                        title={showPasswords[key] ? t('Hide password') : t('Show password')}
                        onClick={() => setShowPasswords((prev) => ({ ...prev, [key]: !prev[key] }))}
                      >
                        {showPasswords[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                ))}
                <Button onClick={changePassword} className="w-full">
                  <Key className="w-4 h-4 mr-2" />
                  {t('Change password')}
                </Button>
                <p className="text-sm text-muted-foreground">
                  {t('Changing your password also signs out every other device.')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Shield className="w-5 h-5" />
                  <span>{t('Active sessions')}</span>
                </CardTitle>
                <CardDescription>
                  {t('Lost a laptop, or think someone else has your password? End every session at once.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t('This signs out every device, including this one. Signing back in takes one password.')}
                </p>
                <Button onClick={signOutEverywhere} variant="destructive" className="w-full">
                  <Shield className="w-4 h-4 mr-2" />
                  {t('Sign out everywhere')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Preferences Tab */}
        <TabsContent value="preferences" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Palette className="w-5 h-5" />
                <span>{t('Interface preferences')}</span>
              </CardTitle>
              <CardDescription>{t('Customize the look and behavior of the app')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>{t('Timezone')}</Label>
                  <Select
                    value={userSettings.timezone}
                    onValueChange={(value) => setUserSettings((prev) => ({ ...prev, timezone: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/New_York">{t('New York (UTC-5)')}</SelectItem>
                      <SelectItem value="America/Chicago">{t('Chicago (UTC-6)')}</SelectItem>
                      <SelectItem value="America/Denver">{t('Denver (UTC-7)')}</SelectItem>
                      <SelectItem value="America/Los_Angeles">{t('Los Angeles (UTC-8)')}</SelectItem>
                      <SelectItem value="America/Sao_Paulo">{t('São Paulo (UTC-3)')}</SelectItem>
                      <SelectItem value="Europe/London">{t('London (UTC+0)')}</SelectItem>
                      <SelectItem value="Europe/Berlin">{t('Berlin (UTC+1)')}</SelectItem>
                      <SelectItem value="Europe/Kiev">{t('Kyiv (UTC+2)')}</SelectItem>
                      <SelectItem value="Europe/Moscow">{t('Moscow (UTC+3)')}</SelectItem>
                      <SelectItem value="Asia/Dubai">{t('Dubai (UTC+4)')}</SelectItem>
                      <SelectItem value="Asia/Kolkata">{t('Mumbai (UTC+5:30)')}</SelectItem>
                      <SelectItem value="Asia/Bangkok">{t('Bangkok (UTC+7)')}</SelectItem>
                      <SelectItem value="Asia/Singapore">{t('Singapore (UTC+8)')}</SelectItem>
                      <SelectItem value="Asia/Tokyo">{t('Tokyo (UTC+9)')}</SelectItem>
                      <SelectItem value="Australia/Sydney">{t('Sydney (UTC+11)')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('Language')}</Label>
                  <Select
                    value={userSettings.language}
                    onValueChange={(value) => {
                      setUserSettings((prev) => ({ ...prev, language: value }));
                      setLocale(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LOCALE_ORDER.map((code) => (
                        <SelectItem key={code} value={code}>{LOCALE_LABELS[code] || code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('Report currency')}</Label>
                  <Select
                    value={userSettings.currency}
                    onValueChange={(value) => setUserSettings((prev) => ({ ...prev, currency: value }))}
                  >
                    <SelectTrigger data-testid="report-currency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map(([code, sign]) => (
                        <SelectItem key={code} value={code}>{`${code} (${sign})`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t('Reports and the dashboard show money in this currency. Exchange rates are updated every hour.')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>{t('Date format')}</Label>
                  <Select
                    value={userSettings.date_format}
                    onValueChange={(value) => setUserSettings((prev) => ({ ...prev, date_format: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DD.MM.YYYY">DD.MM.YYYY</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>{t('Dashboard refresh rate (sec)')}</Label>
                  <Input
                    type="number"
                    value={userSettings.dashboard_refresh_rate}
                    onChange={(e) =>
                      setUserSettings((prev) => ({
                        ...prev,
                        dashboard_refresh_rate: parseInt(e.target.value) || 30,
                      }))
                    }
                    min="10"
                    max="300"
                  />
                </div>
              </div>

              <Separator />

              <Button onClick={updateUserSettings} className="w-full">
                <Save className="w-4 h-4 mr-2" />
                {t('Save settings')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permissions Tab — granular RBAC matrix (Wave 33) */}
        <TabsContent value="permissions" className="space-y-6">
          <PermissionMatrix />
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="system" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Globe className="w-5 h-5" />
                  <span>{t('Tracking domains')}</span>
                </CardTitle>
                <CardDescription>{t('Configure domains for campaign tracking')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t('Tracking domains are managed on the Domains page — add and verify a custom domain there.')}
                </p>
                <Button variant="outline" asChild>
                  <a href="/domains">
                    <Globe className="w-4 h-4 mr-2" />
                    {t('Open Domains')}
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Shield className="w-5 h-5" />
                  <span>{t('Security')}</span>
                </CardTitle>
                <CardDescription>{t('Security and access settings')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center py-8">
                  <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('Security settings')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('Coming in upcoming releases')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
